import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { useSpeak } from "react-text-to-speech";
import {
  requestNotificationPermission,
  subscribeForegroundMessages,
  registerMessagingServiceWorker,
} from "../firebase-config";
import { getCookie } from "../Utility/Utility";
import {
  isOrderPayload,
  isTtsEnabled,
  setTtsEnabled,
  speakDynamicVoice,
  stopSpeaking,
  unlockMobileAudio,
} from "../Utility/textToSpeech";

const usePushNotification = () => {
  const [notification, setNotification] = useState(null);
  const [fcmToken, setFcmToken] = useState(
    typeof window !== "undefined"
      ? localStorage.getItem("kstore_fcm_token") || null
      : null
  );
  const [permission, setPermission] = useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "default"
  );

  // Deduplication state to prevent double announcements
  const lastAnnouncedMessageRef = useRef("");
  const lastAnnouncedTimeRef = useRef(0);
  const lastForegroundMessageKeyRef = useRef("");
  const lastForegroundTimeRef = useRef(0);

  // Initialize react-text-to-speech plugin hook for UI state integration
  const { stop: stopRtts, speechStatus } = useSpeak({
    id: "kstore-order-tts",
    rate: 0.95,
    pitch: 1,
    volume: 1,
    lang: "en-US",
  });

  /**
   * Formats and announces the order notification dynamically.
   * Plays ONLY the clean dynamic speech voice across desktop & mobile.
   */
  const announceOrder = useCallback(
    (body, title = "") => {
      // 1. Never speak on customer pages (kansas_product)
      if (
        typeof window !== "undefined" &&
        window.location.pathname.includes("kansas_product")
      ) {
        console.log("Customer page (kansas_product): skipping speech announcement.");
        return;
      }

      // 2. Check if TTS is enabled
      if (!isTtsEnabled()) {
        console.log("TTS is disabled by user settings.");
        return;
      }

      // 3. Prepare announcement text strictly from the received notification text
      let cleanBody = (body || "").replace(/#(\d+)/g, "number $1").trim();
      let cleanTitle = (title || "").replace(/#(\d+)/g, "number $1").trim();
      let message = cleanBody || cleanTitle || "";

      if (!message) {
        return;
      }

      // Deduplicate voice announcement within 3.5 seconds
      const now = Date.now();
      if (
        message === lastAnnouncedMessageRef.current &&
        now - lastAnnouncedTimeRef.current < 3500
      ) {
        console.log("TTS Announce duplicate skipped:", message);
        return;
      }
      lastAnnouncedMessageRef.current = message;
      lastAnnouncedTimeRef.current = now;

      console.log("TTS Announcing:", message);

      // 4. Trigger direct speech synthesis engineered for mobile Android & iOS
      try {
        speakDynamicVoice(message);
      } catch (err) {
        console.error("Direct dynamic speech error:", err);
      }
    },
    []
  );

  // Expose global test helpers on window for console testing
  useEffect(() => {
    if (typeof window !== "undefined") {
      const runTest = (customText) => {
        announceOrder(customText || "Sethalan added 2 product(s) to wishlist");
      };
      window.testKStoreSpeech = runTest;
      window.textKStoreSpeech = runTest;
    }
  }, [announceOrder]);

  /**
   * Subscribe user to FCM push notifications and register token with backend.
   * Only allowed for franchise logins, never for customer pages (kansas_product).
   */
  const subscribe = useCallback(async (userInfo = null) => {
    if (
      typeof window !== "undefined" &&
      window.location.pathname.includes("kansas_product")
    ) {
      console.log("Customer page (kansas_product): skipping push notification subscription.");
      return null;
    }

    try {
      const token = await requestNotificationPermission();
      if (token) {
        setFcmToken(token);
        localStorage.setItem("kstore_fcm_token", token);
        if ("Notification" in window) {
          setPermission(Notification.permission);
        }
        return token;
      }
    } catch (err) {
      console.error("Error subscribing to push notifications:", err);
    }
    return null;
  }, []);

  // Ensure Firebase Messaging Service Worker is registered on mount (franchise only)
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.location.pathname.includes("kansas_product")
    ) {
      return;
    }
    registerMessagingServiceWorker();
  }, []);

  // Listen for messages while app is in foreground
  useEffect(() => {
    let isCancelled = false;
    let unsubFn = null;

    subscribeForegroundMessages((payload) => {
      if (isCancelled || !payload) return;

      // 1. Only logged-in franchise users should receive notifications
      const userlog = getCookie("userlog");
      if (!userlog || userlog === "null" || userlog === "") {
        console.log("User logged out: skipping push notification.");
        return;
      }

      // 2. Do NOT show notifications or speak if currently on customer pages (kansas_product)
      if (
        typeof window !== "undefined" &&
        window.location.pathname.includes("kansas_product")
      ) {
        console.log("Customer page active (kansas_product): ignoring push notification.");
        return;
      }

      const title =
        payload?.notification?.title ||
        payload?.data?.title ||
        "KStore Notification";
      const body =
        payload?.notification?.body ||
        payload?.data?.body ||
        "You have a new update";
      const targetUrl = payload?.data?.url || "/store";

      // 3. Deduplicate identical foreground messages within 4 seconds
      const messageKey = `${payload?.messageId || ""}_${title}_${body}`;
      const now = Date.now();
      if (
        messageKey === lastForegroundMessageKeyRef.current &&
        now - lastForegroundTimeRef.current < 4000
      ) {
        console.log("Duplicate foreground message skipped:", messageKey);
        return;
      }
      lastForegroundMessageKeyRef.current = messageKey;
      lastForegroundTimeRef.current = now;

      console.log("Foreground message received:", payload);
      setNotification(payload);

      // Trigger dynamic voice announcement for order and wishlist notifications
      if (isOrderPayload(payload)) {
        announceOrder(body, title);
      }

      // Display Native OS system notification banner (macOS / Windows / Android)
      if (
        typeof window !== "undefined" &&
        "Notification" in window &&
        Notification.permission === "granted"
      ) {
        try {
          const orderId =
            payload?.data?.order_id ||
            payload?.data?.orderId ||
            payload?.data?.kansas_order_id ||
            "";
          const cleanId = orderId || `${title}_${body}`;
          const notifTag =
            payload?.data?.tag ||
            `kstore_${cleanId.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 60)}`;

          if (navigator.serviceWorker && navigator.serviceWorker.ready) {
            navigator.serviceWorker.ready.then((reg) => {
              reg.showNotification(title, {
                body: body,
                icon: "/KSTORE_LOGO.png",
                badge: "/logo192.png",
                tag: notifTag,
                renotify: true,
                requireInteraction: true,
                vibrate: [200, 100, 200],
                data: { url: targetUrl, title, body, ...payload?.data },
              });
            });
          } else {
            const n = new Notification(title, {
              body: body,
              icon: "/KSTORE_LOGO.png",
              tag: notifTag,
              renotify: true,
              requireInteraction: true,
            });
            n.onclick = () => {
              window.focus();
              if (targetUrl) window.location.href = targetUrl;
            };
          }
        } catch (err) {
          console.warn("Foreground native notification note:", err);
        }
      }

      // In-app interactive Toast Banner
      toast(
        (t) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              cursor: "pointer",
              width: "100%",
            }}
            onClick={() => {
              // Tap toast to re-announce if mobile OS suspended initial voice
              if (isOrderPayload(payload)) {
                announceOrder(body, title);
              }
              toast.dismiss(t.id);
              if (targetUrl) {
                window.location.href = targetUrl;
              }
            }}
          >
            <span style={{ fontSize: "24px", flexShrink: 0 }}>🔔</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ fontSize: "14px", display: "block" }}>
                {title}
              </strong>
              <span
                style={{
                  fontSize: "12px",
                  opacity: 0.9,
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {body}
              </span>
            </div>
            <button
              type="button"
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                border: "1px solid rgba(255, 255, 255, 0.4)",
                borderRadius: "6px",
                color: "#ffffff",
                padding: "4px 8px",
                fontSize: "12px",
                cursor: "pointer",
                flexShrink: 0,
              }}
              onClick={(e) => {
                e.stopPropagation();
                announceOrder(body, title);
              }}
            >
              🔊 Hear
            </button>
          </div>
        ),
        {
          duration: 7000,
          position: "top-right",
          style: {
            background: "#1f2937",
            color: "#ffffff",
            padding: "12px 16px",
            borderRadius: "8px",
            boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
            maxWidth: "420px",
          },
        }
      );
    }).then((unsub) => {
      if (isCancelled) {
        if (typeof unsub === "function") unsub();
      } else {
        unsubFn = unsub;
      }
    });

    return () => {
      isCancelled = true;
      if (typeof unsubFn === "function") {
        unsubFn();
      }
    };
  }, [announceOrder]);

  /**
   * Unsubscribe and unregister FCM push notifications upon logout.
   */
  const unsubscribePushNotification = useCallback(() => {
    try {
      localStorage.removeItem("kstore_fcm_token");
      setFcmToken(null);
      setNotification(null);
      stopSpeaking();
    } catch (err) {
      console.warn("Unsubscribe push error:", err);
    }
  }, []);

  // Listen for background notification click events delivered via Service Worker
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    const handleSwMessage = (event) => {
      // Ignore if user is logged out
      const userlog = getCookie("userlog");
      if (!userlog || userlog === "null" || userlog === "") return;

      if (event.data?.type === "FCM_NOTIFICATION_CLICKED") {
        const payload = event.data?.payload;
        if (payload) {
          const body = payload?.notification?.body || payload?.data?.body || "";
          const title = payload?.notification?.title || payload?.data?.title || "";
          if (body || title) {
            announceOrder(body, title);
          }
        }
      }
    };

    navigator.serviceWorker.addEventListener("message", handleSwMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleSwMessage);
    };
  }, [announceOrder]);

  // Check if tab was opened from a notification click while the tab was closed
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const ttsParam = urlParams.get("kstore_tts");
      if (ttsParam) {
        // Clean query parameter from browser address bar without reloading
        urlParams.delete("kstore_tts");
        const cleanSearch = urlParams.toString() ? `?${urlParams.toString()}` : "";
        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${cleanSearch}${window.location.hash}`
        );

        const data = JSON.parse(ttsParam);
        if (data && (data.body || data.title || isOrderPayload({ data, notification: data }))) {
          const body = data.body || "";
          const title = data.title || "";
          try {
            unlockMobileAudio();
          } catch (e) {
            // ignore
          }
          // Allow audio pipeline to ready after window creation
          setTimeout(() => {
            announceOrder(body, title);
          }, 400);
        }
      }
    } catch (err) {
      console.warn("Error parsing kstore_tts URL param:", err);
    }
  }, [announceOrder]);

  return {
    permission,
    fcmToken,
    subscribe,
    unsubscribePushNotification,
    notification,
    announceOrder,
    stopSpeaking: stopSpeaking || stopRtts,
    speechStatus,
    isTtsEnabled,
    setTtsEnabled,
  };
};

export default usePushNotification;
